import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DRIVER_TYPE } from 'khufu-legos';
import { CloseButton, Form, Modal as ModalComponent } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import { createDriver } from '../../apis/queries';
import SubmitButton from '../../components/Buttons/SubmitButton';
import { isValidPhoneNumber } from '../../helpers/validation';
import DisplayFieldErrors from '../../utils/DisplayFormError';
import { notification, NotificationKinds } from '../../widgets/Notifications/notifications';
import { GET_DRIVERS_KEY } from './Drivers';

interface CreateDriverModalProps {
  show: boolean;
  handleClose: () => void;
}

interface CreateDriverFormInput {
  name: string;
  nickName: string;
  phoneNumber: string;
  driverType: DRIVER_TYPE;
  isActive: boolean;
}

const CreateDriverModal = ({ show, handleClose }: CreateDriverModalProps) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (driver: CreateDriverFormInput) => {
      return createDriver(driver);
    },
    onSuccess: data => {
      const { data: newDriver } = data;
      notification({
        variant: NotificationKinds.SUCCESS,
        title: `DRIVER CREATED`,
        subtitle: `${newDriver?.name} has been added`
      });

      handleClose();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: GET_DRIVERS_KEY });
    }
  });

  const loading = mutation?.isPending;

  const methods = useForm<CreateDriverFormInput>({
    mode: 'onTouched',
    defaultValues: {
      name: '',
      phoneNumber: '',
      driverType: DRIVER_TYPE.DRIVER,
      nickName: '',
      isActive: true
    }
  });

  const {
    getValues,
    register,
    formState: { isValid, errors }
  } = methods;

  const onCreateDriver = () => {
    const fields = getValues();
    mutation?.mutate(fields);
  };

  return (
    <ModalComponent show={show} onHide={handleClose} centered>
      <ModalComponent.Header>
        <h4 className="card-header-title">New Driver</h4>
        <CloseButton onClick={handleClose} />
      </ModalComponent.Header>
      <ModalComponent.Body className="overflow-auto d-flex flex-column flex-grow-1">
        <Form>
          <div className="form-group">
            <Form.Label htmlFor="name">Name</Form.Label>
            <Form.Control
              type="text"
              id="name"
              placeholder="Mr Chukwudi"
              {...register('name', {
                required: 'Driver Name is required',
                validate: value => value.trim() !== '' || 'Driver Name cannot be empty',
                pattern: {
                  value: /^[a-zA-Z]+( [a-zA-Z]+)*$/,
                  message: 'Driver Name should contain only alphabets'
                }
              })}
              onInput={e => {
                e.currentTarget.value = e.currentTarget.value.replace(/[^a-zA-Z\s]/g, '');
              }}
              autoFocus
            />
            {errors?.name && <DisplayFieldErrors displayText={errors.name.message!} />}
          </div>
          <div className="form-group">
            <Form.Label htmlFor="phoneNumber">Phone Number</Form.Label>
            <Form.Control
              type="tel"
              id="phoneNumber"
              placeholder="09065068997"
              {...register('phoneNumber', {
                required: 'Phone number is required',
                pattern: {
                  value: /^[0-9]+$/,
                  message: 'Phone number must contain only digits'
                },
                validate: isValidPhoneNumber
              })}
              autoFocus
            />
            {errors?.phoneNumber && <DisplayFieldErrors displayText={errors.phoneNumber.message!} />}
          </div>
          <div className="form-group">
            <Form.Label htmlFor="nickName">Nickname</Form.Label>
            <Form.Control
              type="text"
              id="nickName"
              placeholder="chuks"
              {...register('nickName', {
                required: 'Nickname is required',
                validate: value => value.trim() !== '' || 'Nickname cannot be empty',
                pattern: {
                  value: /^[a-zA-Z]+$/,
                  message: 'Nickname should contain only alphabets'
                }
              })}
              onInput={e => {
                e.currentTarget.value = e.currentTarget.value.replace(/[^a-zA-Z\s]/g, '');
              }}
            />
            {errors?.nickName && <DisplayFieldErrors displayText={errors.nickName.message!} />}
          </div>
          <div className="form-group">
            <Form.Label htmlFor="driverType">Driver Type</Form.Label>
            <Form.Select id="driverType" {...register('driverType', { required: true })}>
              <option value="driver">Driver</option>
              <option value="motorboy">Motorboy</option>
            </Form.Select>
            {errors?.driverType && <DisplayFieldErrors displayText={errors.driverType.message!} />}
          </div>

          <SubmitButton
            buttonText="Create Driver"
            loading={loading}
            onClick={onCreateDriver}
            disabled={!isValid || loading}
          ></SubmitButton>
        </Form>
      </ModalComponent.Body>
    </ModalComponent>
  );
};

export default CreateDriverModal;
